import React from 'react';
import { AlertCircleIcon, CheckCircle2Icon, Loader2Icon } from 'lucide-react';
import { PARTNER_CATEGORIES, PARTNERSHIP_INTEREST_OPTIONS } from '../../data/partners';
import { ENTERPRISE_DEMO_URL } from '../../data/dgpt';
import { DESCRIPTION_MAX, fieldId, usePartnerApplicationForm, type ApplicationField } from '../../hooks/usePartnerApplicationForm';
import { cn } from '../../src/lib/utils';
const inputClass = 'mt-1.5 block w-full min-h-[44px] rounded-lg border bg-panel px-3 py-2 text-[15px] text-text-primary placeholder:text-text-tertiary transition-colors focus:outline-none focus:ring-2 focus:ring-lime/30 focus:border-lime';
export function PartnerApplicationForm() {
  const {
    values,
    errors,
    status,
    submitError,
    setField,
    submit,
    reset
  } = usePartnerApplicationForm();
  if (status === 'success') {
    return <div role="status" className="flex flex-col items-start gap-3 rounded-2xl border border-hairline bg-panel p-6 sm:p-8" data-id="element-858" data-mp="2epfv5ge05wec">
        <CheckCircle2Icon className="h-8 w-8 text-[#15803D]" aria-hidden="true" data-id="element-859" data-mp="29ngqm36j0tft" />
        <h3 className="text-xl font-semibold tracking-[-0.02em] text-text-primary" data-id="element-860" data-mp="rcwsa286qeng">Thanks — we’ve received your application.</h3>
        <p className="text-sm leading-relaxed text-text-secondary" data-id="element-861" data-mp="wc9nba97ms14">Our partnerships team will review it and reply to the work email you provided.</p>
        <button type="button" onClick={reset} className="mt-2 text-sm font-semibold text-lime hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-862" data-mp="15c65ct13gsn7">
          Submit another application
        </button>
      </div>;
  }
  const field = (name: ApplicationField, label: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => <div data-id="element-863" data-mp="1o24ezdkjitsy">
      <label htmlFor={fieldId(name)} className="text-sm font-medium text-text-primary" data-id="element-864" data-mp="1o1uozpzhwmn9">
        {label}
      </label>
      <input id={fieldId(name)} name={name} value={values[name]} onChange={event => setField(name, event.target.value)} aria-invalid={Boolean(errors[name])} aria-describedby={errors[name] ? `${fieldId(name)}-error` : undefined} className={cn(inputClass, errors[name] ? 'border-[#D92D20]' : 'border-hairline')} {...props} data-id="element-865" data-mp="1t20ht9hi4f6x" />
      <FieldError id={`${fieldId(name)}-error`} message={errors[name]} data-id="element-866" data-mp="7o47assv4dn8" />
    </div>;
  const submitting = status === 'submitting';
  return <form onSubmit={submit} noValidate className="rounded-2xl border border-hairline bg-panel p-6 sm:p-8" aria-labelledby="partner-form-title" data-id="element-867" data-mp="2usved9716og">
      <h3 id="partner-form-title" className="text-lg font-semibold tracking-[-0.015em] text-text-primary" data-id="element-868" data-mp="cldt7yh1m76b">
        Partner interest form
      </h3>
      <p className="mt-1 text-sm text-text-secondary" data-id="element-869" data-mp="qjuaukgg1i38">All fields are required.</p>

      <div className="mt-6 grid gap-5 sm:grid-cols-2" data-id="element-870" data-mp="lnwsk8lme1nt">
        {field('companyName', 'Company name', {
        autoComplete: 'organization'
      })}
        {field('contactName', 'Contact name', {
        autoComplete: 'name'
      })}
        {field('workEmail', 'Work email', {
        type: 'email',
        autoComplete: 'email',
        inputMode: 'email'
      })}
        {field('website', 'Website', {
        type: 'url',
        autoComplete: 'url',
        inputMode: 'url',
        placeholder: 'example.com'
      })}

        <div className="sm:col-span-2" data-id="element-871" data-mp="t6wb6c19bp64">
          <label htmlFor={fieldId('category')} className="text-sm font-medium text-text-primary" data-id="element-872" data-mp="t5v1obzw3aqi">
            Product category
          </label>
          <select id={fieldId('category')} value={values.category} onChange={event => setField('category', event.target.value)} aria-invalid={Boolean(errors.category)} aria-describedby={errors.category ? `${fieldId('category')}-error` : undefined} className={cn(inputClass, errors.category ? 'border-[#D92D20]' : 'border-hairline')} data-id="element-873" data-mp="k5y6drizvdmh">
            <option value="" data-id="element-874" data-mp="24l9x3bcg9uji">Select a category</option>
            {PARTNER_CATEGORIES.map(category => <option key={category.id} value={category.label} data-id="element-875" data-mp="29l4mfagkuc2g">
                {category.label}
              </option>)}
          </select>
          <FieldError id={`${fieldId('category')}-error`} message={errors.category} data-id="element-876" data-mp="9giv9bvpk37i" />
        </div>

        <div className="sm:col-span-2" data-id="element-877" data-mp="5exa7390sim8">
          <div className="flex items-baseline justify-between" data-id="element-878" data-mp="hfa82czddqd">
            <label htmlFor={fieldId('description')} className="text-sm font-medium text-text-primary" data-id="element-879" data-mp="jru4l2aty62">
              Brief description
            </label>
            <span className={cn('text-xs tabular-nums', values.description.length > DESCRIPTION_MAX ? 'text-[#B42318]' : 'text-text-tertiary')} data-id="element-880" data-mp="3mpyh0xtvigac">
              {values.description.length}/{DESCRIPTION_MAX}
            </span>
          </div>
          <textarea id={fieldId('description')} rows={4} value={values.description} onChange={event => setField('description', event.target.value)} aria-invalid={Boolean(errors.description)} aria-describedby={errors.description ? `${fieldId('description')}-error` : undefined} placeholder="What does your product or service do, and who is it for?" className={cn(inputClass, 'resize-y', errors.description ? 'border-[#D92D20]' : 'border-hairline')} data-id="element-881" data-mp="3duh1vxu1lsz6" />
          <FieldError id={`${fieldId('description')}-error`} message={errors.description} data-id="element-882" data-mp="2yuv0i6r2oz7t" />
        </div>

        <fieldset className="sm:col-span-2" aria-describedby={errors.interest ? `${fieldId('interest')}-error` : undefined} data-id="element-883" data-mp="33rlkh5600eyi">
          <legend className="text-sm font-medium text-text-primary" data-id="element-884" data-mp="2631nspa82vvd">Partnership interest</legend>
          <div className="mt-2 flex flex-wrap gap-2" data-id="element-885" data-mp="2b24su9q7d23c">
            {PARTNERSHIP_INTEREST_OPTIONS.map((option, index) => {
            const checked = values.interest === option;
            return <label key={option} className={cn('inline-flex min-h-[40px] cursor-pointer items-center rounded-full border px-4 text-sm font-medium transition-colors focus-within:ring-2 focus-within:ring-lime focus-within:ring-offset-2', checked ? 'border-lime bg-lime text-ink' : 'border-hairline bg-panel text-text-secondary hover:border-lime/40 hover:text-text-primary')} data-id="element-886" data-mp="2kw530q0ccxix" data-mpi={index}>
                  <input id={index === 0 ? fieldId('interest') : undefined} type="radio" name="interest" value={option} checked={checked} onChange={() => setField('interest', option)} className="sr-only" data-id="element-887" data-mp="1u4xx4vtlyab6" />
                  {option}
                </label>;
          })}
          </div>
          <FieldError id={`${fieldId('interest')}-error`} message={errors.interest} data-id="element-888" data-mp="16d0unvc1sumq" />
        </fieldset>
      </div>

      {status === 'error' ? <div role="alert" className="mt-6 flex gap-3 rounded-xl border border-[#FDA29B] bg-[#FEF3F2] p-4 text-sm text-[#7A271A]" data-id="element-889" data-mp="2mtmz0x2hmjnz">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-890" data-mp="2pbfc783gfk2s" />
          <p data-id="element-891" data-mp="2ub1qlkv0r6d6">
            {submitError === 'unavailable' ? 'Online applications aren’t available right now. ' : 'We couldn’t send your application. Please try again in a moment, or '}
            <a href={ENTERPRISE_DEMO_URL} target="_blank" rel="noopener noreferrer" className="font-semibold underline underline-offset-2" data-id="element-892" data-mp="2kcm0nl7qa359">
              book a call with our team
            </a>
            {submitError === 'unavailable' ? ' to discuss a partnership.' : '.'}
          </p>
        </div> : null}

      <button type="submit" disabled={submitting} className="mt-6 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-lime px-6 text-[15px] font-semibold text-ink shadow-sm transition-colors hover:bg-[#2B1E7A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto" data-id="element-893" data-mp="2zaxr3a4icfnz">
        {submitting ? <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-894" data-mp="373hc82zl1vi0" /> : null}
        {submitting ? 'Sending…' : 'Submit Application'}
      </button>
    </form>;
}
function FieldError({
  id,
  message
}: {
  id: string;
  message?: string;
}) {
  if (!message) return null;
  return <p id={id} className="mt-1.5 text-xs text-[#B42318]" data-id="element-895" data-mp="2se1q50azye8n">
      {message}
    </p>;
}